import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import authService from '../services/authService';

const UserContext = createContext(null);

const parseTokenPayload = (token) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    return null;
  }
};

const isTokenExpired = (token) => {
  const payload = parseTokenPayload(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 <= Date.now();
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  });

  const clearUser = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      clearUser();
      return null;
    }
    if (isTokenExpired(token)) {
      clearUser();
      return null;
    }

    try {
      const response = await authService.me();
      const data = response?.data ?? response;
      const newToken = data?.token ?? data?.accessToken;
      if (newToken && typeof newToken === 'string') {
        localStorage.setItem('authToken', newToken);
      }
      const refreshedUser = data?.user ?? (data && !data.token && !data.accessToken ? data : null);
      if (refreshedUser) {
        localStorage.setItem('user', JSON.stringify(refreshedUser));
        setUser(refreshedUser);
      }
      return refreshedUser ?? null;
    } catch (error) {
      if (error?.response?.status === 401) {
        clearUser();
      }
      return null;
    }
  }, [clearUser]);

  const value = useMemo(() => ({
    user,
    setUser,
    refreshUser,
    clearUser,
    isTokenExpired
  }), [user, refreshUser, clearUser]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
